import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * 下拉刷新。
 *
 * ⚠️ 它必须绑在**该响应的滚动容器**上，不能绑在页面上——绑在页面上就会和浏览器自带的
 * 下拉刷新打架，玩两下就把整个页面刷新了（用户输入的东西全没）。
 *
 * 另外两条：
 * - 只有 `scrollTop === 0` 时才开始，否则往下滚一半就触发刷新；
 * - 阻尼曲线：手指移动 100px 只下拉 ~55px。线性跟随会让人觉得"页面松了"。
 */
import { useCallback, useRef, useState } from 'react';
const MAX_PULL = 140;
export function PullToRefresh({ onRefresh, children, threshold = 72, disabled = false, className = '', labels, }) {
    const scrollRef = useRef(null);
    const start = useRef(null);
    const [pull, setPull] = useState(0);
    const [refreshing, setRefreshing] = useState(false);
    const text = refreshing
        ? (labels?.refreshing ?? '正在刷新')
        : pull >= threshold
            ? (labels?.release ?? '松开刷新')
            : (labels?.pull ?? '下拉刷新');
    const onPointerDown = useCallback((e) => {
        if (disabled || refreshing || e.button !== 0)
            return;
        /*
         * ⚠️ 只认手指。
         *
         * 下拉刷新是一个**触摸手势**：鼠标用户没有"下拉"这个习惯，而用鼠标按住往下拖
         * 通常是在**选文字**——不判这一条，桌面上选一段文字就会触发一次刷新，
         * 而用户完全不知道自己做了什么。
         *
         * 判据用 `e.pointerType`（这一次交互是什么设备），不是 `usePointerCoarse()`
         * （这台设备有没有触摸能力）：触屏笔记本两样都有，要看的是**这一下是谁点的**。
         *
         * 桌面上刷新必须另有入口（一个按钮 / mod+R），不能只有这个手势。
         */
        if (e.pointerType === 'mouse')
            return;
        // 只在真的滚到顶时才接管。不判这条，往下滚到一半也会触发。
        if ((scrollRef.current?.scrollTop ?? 0) > 0)
            return;
        start.current = e.clientY;
    }, [disabled, refreshing]);
    const onPointerMove = useCallback((e) => {
        if (start.current === null)
            return;
        const dy = e.clientY - start.current;
        if (dy <= 0) {
            setPull(0);
            return;
        }
        // 阻尼：越拉越沉，且有硬上限
        setPull(Math.min(MAX_PULL, dy * 0.55));
    }, []);
    const finish = useCallback(async () => {
        if (start.current === null)
            return;
        start.current = null;
        if (pull < threshold) {
            setPull(0);
            return;
        }
        setRefreshing(true);
        setPull(threshold);
        try {
            await onRefresh();
        }
        catch {
            // 刷新失败也要收回去，否则指示器永远转着，用户以为卡死了。
            // 具体的失败提示由调用方在 onRefresh 里给（Toast），这里不吞掉也不代劳。
        }
        finally {
            setRefreshing(false);
            setPull(0);
        }
    }, [pull, threshold, onRefresh]);
    return (_jsxs("div", { className: `ps-ptr ${className}`.trim(), children: [_jsxs("div", { className: "ps-ptr-indicator", style: { height: pull, opacity: pull > 4 ? 1 : 0 }, "aria-hidden": pull === 0, children: [_jsx("span", { className: `ps-ptr-spinner ${refreshing ? 'ps-spinning' : ''}`.trim(), style: { transform: `rotate(${pull * 3}deg)` } }), _jsx("span", { className: "ps-ptr-text", children: text })] }), _jsx("div", { ref: scrollRef, className: "ps-scroll ps-ptr-body", style: {
                    transform: pull ? `translateY(${pull}px)` : undefined,
                    transition: start.current === null ? 'transform var(--ps-t-base) var(--ps-ease-out)' : 'none',
                }, onPointerDown: onPointerDown, onPointerMove: onPointerMove, onPointerUp: () => void finish(), onPointerCancel: () => void finish(), children: children })] }));
}
//# sourceMappingURL=pull-to-refresh.js.map